import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import request from "../../utils/http";
import toaster from "../../utils/toaster";
import { Spinner } from "baseui/spinner";
import ActionHeader from "../../components/ActionHeader";
import { Button } from "baseui/button";
import ActionBar from "../../components/ActionBar";
import { FormControl } from "baseui/form-control";
import { Radio, RadioGroup } from "baseui/radio";
import { Checkbox } from "baseui/checkbox";
import DateTimeText from "../../components/DateTimeText";
import { TimePicker } from "baseui/timepicker";
import { DatePicker } from "baseui/datepicker";
import {
	dateFromDateArray,
	zonelessIsoDate,
	zonelessIsoTimeOnly,
} from "../../utils/date";

const Container = styled.div``;

const CandidateOffer = () => {
	const navigate = useNavigate();
	const urlParams = useParams() as any;

	const [candidateId, setCandidateId] = useState("");

	const [data, setData] = useState(null as any);

	const [loaded, setLoaded] = useState(false);

	const [files, setFiles] = useState([] as any);

	const [signature, setSignature] = useState("");
	const [permissionConfirmed, setPermissionConfirmed] = useState(false);

	const [hoursKind, setHoursKind] = useState("Fixed");

	const [startTime, setStartTime] = useState<Date | null>(null);

	const [endTime, setEndTime] = useState<Date | null>(null);

	const [effectiveDate, setEffectiveDate] = React.useState<Date | Date[]>([]);

	const [fixedTermEndDate, setFixedTermEndDate] = React.useState<Date | Date[]>(
		[]
	);

	const [saving, setSaving] = useState(false);

	const loadFiles = async () => {
		let result = await request(
			"GET",
			`/api/admin/asset-file/all/Signature`,
			null
		);
		if (result.success && result.data) {
			setFiles(result.data.files);
		}
	};

	const updateUrlParams = async () => {
		setCandidateId(urlParams.candidateId);
	};

	const getCandidate = async () => {
		let result = await request(
			"GET",
			`/api/admin/candidate/${candidateId}`,
			null,
			true,
			false
		);
		if (result.success && result.data?.candidateId) {
			setData(result.data);

			setLoaded(true);
		} else {
			toaster.error("Unable to access candidate");
		}
	};

	const makeOffer = async () => {
		if (signature && signature != "" && permissionConfirmed) {
			setSaving(true);

			let result = await request(
				"POST",
				`/api/admin/candidate-offer/${candidateId}`,
				{
					signatureFileId: signature,
					hoursKind,
					startTime: zonelessIsoTimeOnly(dateFromDateArray(startTime)),
					endTime: zonelessIsoTimeOnly(dateFromDateArray(endTime)),
					effectiveDate: zonelessIsoDate(dateFromDateArray(effectiveDate)),
					fixedTermEndDate: zonelessIsoDate(
						dateFromDateArray(fixedTermEndDate)
					),
				},
				true,
				false
			);

			if (result.success) {
				toaster.success("Offer made successfully!");
				navigate(`/candidate/${candidateId}`);
			} else {
				toaster.error("Unable to make offer to candidate");
				setSaving(false);
			}
		} else {
			toaster.error(
				"You must select a signature and confirm permission to make an offer"
			);
		}
	};

	useEffect(() => {
		if (candidateId != "") {
			getCandidate();
		}
		loadFiles();
	}, [candidateId]);

	useEffect(() => {
		updateUrlParams();
	}, [urlParams, updateUrlParams]);

	return (
		<Container>
			{!loaded && (
				<div>
					<Spinner />
				</div>
			)}
			{loaded && data && (
				<>
					{data.fields["CandidateAgreement.JobOfferMade"] ? (
						<>
							<ActionHeader>
								<h1>
									{data.fields["Candidate.FirstName"]}{" "}
									{data.fields["Candidate.LastName"]}'s Job Offer
								</h1>
								<ActionBar>
									<Button
										kind="secondary"
										onClick={() => {
											navigate(`/candidate/${candidateId}`);
										}}
									>
										Go back
									</Button>
								</ActionBar>
							</ActionHeader>
							<hr />
							<div>
								<ul>
									<li>
										Offer made by{" "}
										{data.fields["CandidateAgreement.JobOfferMadeBy"]} on{" "}
										<DateTimeText
											utcDateTime={
												data.fields["CandidateAgreement.JobOfferMadeDate"]
											}
											includeRelative={true}
										/>
									</li>
									{data.fields["CandidateAgreement.JobOfferCompleted"] && (
										<li>
											Offer accepted by {data.fields["Candidate.FirstName"]}{" "}
											{data.fields["Candidate.LastName"]} on{" "}
											<DateTimeText
												utcDateTime={
													data.fields[
														"CandidateAgreement.JobOfferCompletedDate"
													]
												}
												includeRelative={true}
											/>
										</li>
									)}
								</ul>
							</div>
						</>
					) : (
						<>
							<ActionHeader>
								<h1>
									Make Offer to {data.fields["Candidate.FirstName"]}{" "}
									{data.fields["Candidate.LastName"]}
								</h1>
								<ActionBar>
									<Button
										kind="secondary"
										onClick={() => {
											navigate(`/candidate/${candidateId}`);
										}}
									>
										Go back
									</Button>
								</ActionBar>
							</ActionHeader>
							<hr />
							<div>
								<p>
									<strong>
										Are you sure you wish to make an offer to{" "}
										{data.fields["Candidate.FirstName"]}{" "}
										{data.fields["Candidate.LastName"]}?
									</strong>
								</p>
							</div>
							<hr />
							<div>
								<FormControl label={"Who is signing this agreement?"}>
									<>
										<RadioGroup
											value={signature}
											onChange={(e) => setSignature(e.currentTarget.value)}
										>
											{files &&
												files.map((file: any) => {
													return <Radio value={file.id}>{file.name}</Radio>;
												})}
										</RadioGroup>
									</>
								</FormControl>
							</div>
							<div>
								<FormControl
									label={`Do you have permission to sign on ${
										signature != ""
											? files.find((file: any) => {
													return file.id == signature;
											  }).name + "'s"
											: "their"
									} behalf?`}
								>
									<>
										<Checkbox
											checked={permissionConfirmed}
											onChange={() => {
												setPermissionConfirmed(!permissionConfirmed);
											}}
										>
											I have permission to sign on{" "}
											{signature != ""
												? files.find((file: any) => {
														return file.id == signature;
												  }).name + "'s"
												: "their"}{" "}
											behalf
										</Checkbox>
									</>
								</FormControl>
							</div>
							<hr />
							<div>
								<FormControl label={"Working hours type"}>
									<RadioGroup
										onChange={(e) => setHoursKind(e.target.value)}
										value={hoursKind}
									>
										<Radio value="Fixed">Fixed hours</Radio>
										<Radio value="AsDetailed">
											As detailed in the table in Addendum 1/ Employment Agreement (varied)
										</Radio>
										<Radio value="TBC">To be confirmed</Radio>
									</RadioGroup>
								</FormControl>
							</div>
							{hoursKind == "Fixed" && (
								<>
									<div>
										<FormControl label={"Start Time"}>
											<div style={{ width: "10rem" }}>
												<TimePicker
													value={startTime}
													onChange={setStartTime}
													creatable={true}
												/>
											</div>
										</FormControl>
									</div>
									<div>
										<FormControl label={"End Time"}>
											<div style={{ width: "10rem" }}>
												<TimePicker
													value={endTime}
													onChange={setEndTime}
													creatable={true}
												/>
											</div>
										</FormControl>
									</div>
									<div>
										<em>
											Note: This time is exclusive of a 30 minute unpaid meal
											break
										</em>
									</div>
								</>
							)}
							<hr />
							<div>
								<FormControl label={"Effective date (start date)"}>
									<div style={{ width: "20rem" }}>
										<DatePicker
											value={effectiveDate || null}
											onChange={({ date }) => setEffectiveDate(date)}
											formatString="dd/MM/yyyy"
											placeholder=""
											mask="99/99/9999"
										/>
									</div>
								</FormControl>
							</div>
							{data?.fields["CandidateInternal.EmploymentType.ToString"] ===
								"FixedTerm" && (
								<div>
									<FormControl label={"End Date (Fixed Term)"}>
										<div style={{ width: "20rem" }}>
											<DatePicker
												value={fixedTermEndDate || null}
												onChange={({ date }) => setFixedTermEndDate(date)}
												formatString="dd/MM/yyyy"
												placeholder=""
												mask="99/99/9999"
											/>
										</div>
									</FormControl>
								</div>
							)}
							<div>
								<br />
								<br />
							</div>
							<div>
								<Button onClick={makeOffer} isLoading={saving}>
									Make Offer to {data.fields["Candidate.FirstName"]}{" "}
									{data.fields["Candidate.LastName"]}
								</Button>
							</div>
							<div>
								<br />
								<br />
								<em>
									Once the offer is made, {data.fields["Candidate.FirstName"]}{" "}
									will automatically be emailed with a pre-signed copy of their
									contract, and will be able to log back in and sign online. You
									will be copied in to the email.
								</em>
							</div>
						</>
					)}
				</>
			)}
		</Container>
	);
};
export default CandidateOffer;
